import React from "react";
import { useFormContext } from "../FormProvider";
import { CiCircleInfo } from "react-icons/ci";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "../../../ui/form";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../ui/tooltip";
import { FieldBaseProps } from "../types";

export const withFormField = <P extends FieldBaseProps>(
  Component: React.ComponentType<P>,
) => {
  return (props: React.PropsWithChildren<P>) => {
    const {
      name,
      label,
      description,
      tooltip,
      required = false,
      ...rest
    } = props;
    const { control, disabled, readOnly } = useFormContext();

    const renderTooltip = () => {
      if (!tooltip) {
        return <></>;
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <CiCircleInfo size={16} />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      );
    };

    const renderDescription = () => {
      if (!description) {
        return <></>;
      }

      return <FormDescription>{description}</FormDescription>;
    };

    return (
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <FormItem className="flex flex-col gap-2">
            {label && (
              <FormLabel>
                <span className="flex items-center gap-1">
                  {label}
                  {required && <span className="text-purple-muted-300">*</span>}
                  {!!tooltip && renderTooltip()}
                </span>
              </FormLabel>
            )}
            <div className="flex flex-col gap-1">
              <FormControl>
                <Component
                  {...field}
                  disabled={disabled}
                  {...(rest as P)}
                  readOnly={readOnly}
                  error={!!fieldState.error}
                />
              </FormControl>
              {!!description && renderDescription()}
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
    );
  };
};
